import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  getProviders,
  createProvider,
  updateProvider,
  updateProviderKey,
  revealProviderKey,
  activateProvider,
  deactivateProvider,
  updateRateLimits,
  deleteProvider,
  type AIProvider,
} from '@/services/admin-api';
import type { CreateProviderInput, UpdateProviderInput, UpdateRateLimitsInput } from '@/lib/schemas/ai-schemas';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ProviderTable } from '@/components/ai-providers/ProviderTable';
import { CreateProviderDialog } from '@/components/ai-providers/CreateProviderDialog';
import { EditProviderDialog } from '@/components/ai-providers/EditProviderDialog';
import { UpdateKeyDialog } from '@/components/ai-providers/UpdateKeyDialog';
import { RevealKeyDialog } from '@/components/ai-providers/RevealKeyDialog';
import { RateLimitsDialog } from '@/components/ai-providers/RateLimitsDialog';

type DialogType = 'create' | 'edit' | 'updateKey' | 'revealKey' | 'rateLimits';

export default function AIProviders() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [dialogType, setDialogType] = useState<DialogType | null>(null);
  const [activeProvider, setActiveProvider] = useState<AIProvider | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AIProvider | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: getProviders,
  });

  const providers = data?.data ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ai-providers'] });

  function closeDialog() {
    setDialogType(null);
    setActiveProvider(null);
    setRevealedKey(null);
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateProviderInput) => createProvider(data),
    onSuccess: () => { invalidate(); toast({ title: 'Provider created' }); closeDialog(); },
    onError: (error: Error) => toast({ title: 'Failed to create provider', description: error.message, variant: 'destructive' }),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProviderInput }) => updateProvider(id, data),
    onSuccess: () => { invalidate(); toast({ title: 'Provider updated' }); closeDialog(); },
    onError: (error: Error) => toast({ title: 'Failed to update provider', description: error.message, variant: 'destructive' }),
  });

  const keyMutation = useMutation({
    mutationFn: ({ id, apiKey }: { id: string; apiKey: string }) => updateProviderKey(id, apiKey),
    onSuccess: () => { invalidate(); toast({ title: 'API key updated' }); closeDialog(); },
    onError: (error: Error) => toast({ title: 'Failed to update key', description: error.message, variant: 'destructive' }),
  });

  const rateLimitsMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRateLimitsInput }) => updateRateLimits(id, data),
    onSuccess: () => { invalidate(); toast({ title: 'Rate limits updated' }); closeDialog(); },
    onError: (error: Error) => toast({ title: 'Failed to update rate limits', description: error.message, variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: (provider: AIProvider) =>
      provider.isActive ? deactivateProvider(provider.id) : activateProvider(provider.id),
    onSuccess: (_, provider) => { invalidate(); toast({ title: provider.isActive ? 'Provider deactivated' : 'Provider activated' }); },
    onError: (error: Error) => toast({ title: 'Failed to toggle provider', description: error.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProvider(id),
    onSuccess: () => { invalidate(); toast({ title: 'Provider deleted' }); setDeleteTarget(null); },
    onError: (error: Error) => toast({ title: 'Failed to delete provider', description: error.message, variant: 'destructive' }),
  });

  const handleRevealKey = async (provider: AIProvider) => {
    try {
      setActiveProvider(provider);
      setDialogType('revealKey');
      const response = await revealProviderKey(provider.id);
      setRevealedKey(response.data.apiKey);
    } catch (error) {
      toast({ title: 'Failed to reveal key', description: (error as Error).message, variant: 'destructive' });
      closeDialog();
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="AI Providers"
        description="Manage AI provider configurations and API keys"
        actions={
          <Button onClick={() => setDialogType('create')}>
            <Plus className="mr-2 h-4 w-4" />
            Add Provider
          </Button>
        }
      />

      <ProviderTable
        providers={providers}
        isTogglePending={toggleMutation.isPending}
        onToggle={(p) => toggleMutation.mutate(p)}
        onEdit={(p) => { setActiveProvider(p); setDialogType('edit'); }}
        onUpdateKey={(p) => { setActiveProvider(p); setDialogType('updateKey'); }}
        onRevealKey={handleRevealKey}
        onRateLimits={(p) => { setActiveProvider(p); setDialogType('rateLimits'); }}
        onDelete={(p) => setDeleteTarget(p)}
      />

      <CreateProviderDialog
        isOpen={dialogType === 'create'}
        onClose={closeDialog}
        onSubmit={(data) => createMutation.mutate(data)}
        isPending={createMutation.isPending}
      />

      <EditProviderDialog
        isOpen={dialogType === 'edit'}
        onClose={closeDialog}
        provider={activeProvider}
        onSubmit={(data) => editMutation.mutate({ id: activeProvider!.id, data })}
        isPending={editMutation.isPending}
      />

      <UpdateKeyDialog
        isOpen={dialogType === 'updateKey'}
        onClose={closeDialog}
        providerName={activeProvider?.name ?? null}
        onSubmit={(apiKey) => keyMutation.mutate({ id: activeProvider!.id, apiKey })}
        isPending={keyMutation.isPending}
      />

      <RevealKeyDialog
        isOpen={dialogType === 'revealKey'}
        onClose={closeDialog}
        providerName={activeProvider?.name ?? null}
        revealedKey={revealedKey}
      />

      <RateLimitsDialog
        isOpen={dialogType === 'rateLimits'}
        onClose={closeDialog}
        provider={activeProvider}
        onSubmit={(data) => rateLimitsMutation.mutate({ id: activeProvider!.id, data })}
        isPending={rateLimitsMutation.isPending}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Provider"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will also remove associated pricing and prompt configurations.`}
        onConfirm={() => deleteMutation.mutate(deleteTarget!.id)}
        loading={deleteMutation.isPending}
        data-testid="delete-dialog"
      />
    </div>
  );
}
